"use client";

import { useCallback } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { Building2, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CompanyDetailsTab } from "@/components/shared/detail-tabs/CompanyDetailsTab";
import { ClientInfoDisplay } from "@/features/contacts/components/ClientInfoDisplay";
import { CompanyContacts } from "@/features/contacts/components/CompanyContacts";
import { useCompany, useCompanyContacts } from "@/lib/hooks/api/companies";
import { useContact } from "@/lib/hooks/use-api";
import { useModalControls } from "@/lib/modals";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { ContactTaskRow } from "@/lib/types/entity-rows";

export function TaskCompanyDisplay({ item }: TabContentProps<ContactTaskRow>) {
  const router = useRouter();
  const taskContactId = item.contact_id || "";

  const {
    data: fullContact,
    isLoading: isContactLoading,
    error: contactError,
  } = useContact(taskContactId, { enabled: !!taskContactId });

  const resolvedContact = fullContact || (item as any).contacts || null;
  const companyId = resolvedContact?.company_id || null;
  const companyName = resolvedContact?.company_name || null;

  const {
    data: company,
    isLoading: isCompanyLoading,
    error: companyError,
  } = useCompany(companyId, { enabled: !!companyId });

  const { isLoading: isCompanyPeopleLoading } = useCompanyContacts(companyId, { limit: 20 });
  const { openModal: openCompanyModal } = useModalControls("companies-detail");

  const handleViewFullCompany = useCallback(() => {
    if (companyId) {
      openCompanyModal({ companiesId: companyId });
    } else if (companyName) {
      router.push(`/companies?search=${encodeURIComponent(companyName)}`);
    }
  }, [router, companyId, companyName, openCompanyModal]);

  if (!taskContactId) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8 text-gray-500 space-y-3">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No contact is linked to this task</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() => router.push("/contacts")}
              className="h-11 sm:h-8"
            >
              Open Contacts
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (isContactLoading || isCompanyLoading || isCompanyPeopleLoading) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>Loading company details...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (contactError || companyError) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8 text-red-600 dark:text-red-400">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-red-300" />
            <p>Unable to load company details for this task</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!companyName && !companyId) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8 text-gray-500 space-y-3">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>The assigned contact has no company linked</p>
            <Button
              variant="outline"
              size="sm"
              onClick={() =>
                router.push(
                  resolvedContact?.id ? `/contacts/${resolvedContact.id}?edit=true` : "/contacts"
                )
              }
              className="h-11 sm:h-8"
            >
              Update Contact Company
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="w-full space-y-4">
      <CompanyDetailsTab companyData={company} onViewFullCompany={handleViewFullCompany} />

      <div className="border-t pt-4">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
          <h4 className="text-base font-semibold text-card-foreground">Contact</h4>
          {resolvedContact?.id ? (
            <Button
              variant="outline"
              size="sm"
              asChild
              className="gap-2 shrink-0 h-11 sm:h-8 w-full sm:w-auto"
            >
              <Link href={`/contacts/${resolvedContact.id}`}>
                <ExternalLink className="w-4 h-4" />
                View full contact
              </Link>
            </Button>
          ) : null}
        </div>
        {resolvedContact ? (
          <ClientInfoDisplay
            client={resolvedContact}
            sections={["contact", "contract", "address"]}
            variant="default"
            showTitles={true}
            className="grid-cols-1"
          />
        ) : (
          <p className="text-sm text-gray-500">Contact details unavailable</p>
        )}
      </div>

      <CompanyContacts
        companyName={company?.name || companyName || "Unknown Company"}
        currentContactId={taskContactId}
        currentContact={resolvedContact || undefined}
      />
    </div>
  );
}
